/**
 * src/lib/playbookModel.js — mô hình dữ liệu bài Playbook, dạng hàm thuần.
 *
 * Không import Firebase. Dùng chung ở ba nơi và phải cho kết quả giống nhau:
 *  - trình duyệt (trang bài, trang quản trị)
 *  - scripts/fetch-articles.mjs lúc build (kéo bài đã xuất bản về để prerender)
 *  - src/data/playbook.js (chuẩn hoá bài đóng gói sẵn)
 */

/**
 * Năm loạt bài của Playbook (menu chính). Mỗi chủ đề thuộc đúng một loạt.
 * Slug 'cam-nang' và 'cau-chuyen' giữ nguyên để không đổi URL Google đã lập chỉ mục;
 * 'cau-chuyen' nay mang tên "Tình huống thực tế".
 * Loạt "Nhìn lại" luôn có ngày chốt số liệu trong bài.
 */
export const TRACKS = {
  'cam-nang': { label: 'Cẩm nang gọi vốn', slug: 'cam-nang', lead: 'Hướng dẫn từng bước, đọc lúc nào cũng dùng được: mô hình tài chính, định giá, cap table, pitch deck, tiếp cận nhà đầu tư.' },
  'cau-chuyen': { label: 'Tình huống thực tế', slug: 'cau-chuyen', lead: 'Mổ xẻ một thương vụ hoặc một công ty cụ thể: họ gọi bao nhiêu, từ ai, điều gì quyết định kết quả.' },
  'kinh-nghiem': { label: 'Kinh nghiệm gọi vốn', slug: 'kinh-nghiem', lead: 'Sai lầm thường gặp, câu hỏi quỹ hay đặt ra và góc nhìn từ phía nhà đầu tư.' },
  'nhin-lai': { label: 'Nhìn lại tuần, tháng', slug: 'nhin-lai', lead: 'Thương vụ, xu hướng và chính sách đáng chú ý trong tuần hoặc tháng, kèm ngày chốt số liệu và bài học cho startup Việt.' },
  'chinh-sach': { label: 'Chính sách & ưu đãi', slug: 'chinh-sach', lead: 'Văn bản pháp lý, ưu đãi thuế, quỹ và chương trình hỗ trợ của nhà nước, giải thích theo từng bước làm.' },
};

/** Chủ đề, mỗi chủ đề thuộc một loạt bài. */
export const CATEGORIES = [
  { name: 'Mô hình tài chính', track: 'cam-nang' },
  { name: 'Báo cáo tài chính', track: 'cam-nang' },
  { name: 'Định giá', track: 'cam-nang' },
  // Cap table, ESOP, vesting, pha loãng, chia cổ phần đồng sáng lập.
  { name: 'Cổ phần & cap table', track: 'cam-nang' },
  // SAFE, trái phiếu chuyển đổi, cổ phần ưu đãi, vốn vay, tài trợ không hoàn lại.
  { name: 'Các loại vốn', track: 'cam-nang' },
  { name: 'Hồ sơ & pitch deck', track: 'cam-nang' },
  { name: 'Tiếp cận nhà đầu tư', track: 'cam-nang' },
  { name: 'Due diligence', track: 'cam-nang' },
  { name: 'Tình huống gọi vốn', track: 'cau-chuyen' },
  // Giới thiệu startup đang gọi vốn (đợt pilot 10/2026). Founder duyệt bản cuối.
  { name: 'Startup Spotlight', track: 'cau-chuyen' },
  { name: 'Sai lầm thường gặp', track: 'kinh-nghiem' },
  { name: 'Câu hỏi của quỹ', track: 'kinh-nghiem' },
  { name: 'Góc nhìn quỹ', track: 'kinh-nghiem' },
  { name: 'Nhìn lại tuần', track: 'nhin-lai' },
  { name: 'Nhìn lại tháng', track: 'nhin-lai' },
  { name: 'Chính sách & ưu đãi', track: 'chinh-sach' },
  { name: 'Chương trình hỗ trợ nhà nước', track: 'chinh-sach' },
];

export const PLAYBOOK_CATEGORIES = CATEGORIES.map((c) => c.name);
export const categoriesOfTrack = (track) => CATEGORIES.filter((c) => c.track === track).map((c) => c.name);
export const trackOfCategory = (name) => CATEGORIES.find((c) => c.name === name)?.track || 'cam-nang';

export const PLAYBOOK_STAGES = ['Idea', 'Pre-seed', 'Seed', 'Series A'];

/**
 * Loại bài. Ghi rõ cho người đọc biết đây là chuyện thật hay tình huống dựng
 * lại — trang hứa "tình huống thật" thì phải trung thực về điều đó.
 */
export const STORY_TYPES = {
  'cau-chuyen': { label: 'Câu chuyện founder', note: '' },
  'goc-nhin': { label: 'Góc nhìn chuyên gia', note: '' },
  'tong-hop': { label: 'Tình huống minh họa', note: 'Tình huống được tổng hợp từ nhiều trường hợp; tên và số liệu đã được thay đổi.' },
  'huong-dan': { label: 'Hướng dẫn thực hành', note: '' },
  spotlight: { label: 'Startup Spotlight', note: 'Nội dung do founder cung cấp và đã duyệt trước khi đăng. Số liệu ghi "founder tự khai" chưa được fundraising.vn đối chiếu. Được giới thiệu ở đây không có nghĩa fundraising.vn thẩm định hay khuyến nghị đầu tư.' },
};

/** Loại bài cần nhân vật duyệt bản cuối trước khi xuất bản (kiểm tra cả ở Firestore Rules). */
export const CAN_DONG_Y = ['cau-chuyen', 'spotlight'];

/** Slug dành riêng cho hệ thống — không cho bài viết dùng. */
export const RESERVED_SLUGS = ['chu-de', '_ban-moi', 'gop-cau-chuyen', ...Object.keys(TRACKS)];

export function chuanHoaSlug(value) {
  return String(value || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 100);
}

export const categorySlug = (category) => chuanHoaSlug(category);
export const categoryBySlug = (slug) => PLAYBOOK_CATEGORIES.find((c) => categorySlug(c) === slug) || null;

function plain(value, max = 4000) { return String(value ?? '').trim().slice(0, max); }
function httpsUrl(value) { const url = plain(value, 1000); return /^https:\/\//.test(url) ? url : ''; }
function isoDate(value) {
  if (!value) return '';
  if (typeof value?.toDate === 'function') return value.toDate().toISOString().slice(0, 10);
  const s = String(value);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : '';
}

function chuanHoaAnh(raw) {
  const url = httpsUrl(raw?.url);
  if (!url) return null;
  return {
    url,
    alt: plain(raw.alt, 200),
    caption: plain(raw.caption, 300),
    credit: plain(raw.credit, 120),
    width: Math.max(0, Math.round(Number(raw.width) || 0)),
    height: Math.max(0, Math.round(Number(raw.height) || 0)),
    // 'rong' = cắt 16:9 cho đều nhịp; 'nguyen-ban' = giữ tỷ lệ (ảnh chụp slide, bảng số).
    fit: raw.fit === 'nguyen-ban' ? 'nguyen-ban' : 'rong',
    position: raw.position === 'dau' ? 'dau' : 'cuoi',
    // Bản hẹp (tuỳ chọn) cho điện thoại, ví dụ hình hai khung xếp dọc thay vì cạnh nhau.
    ...(httpsUrl(raw.urlHep) ? { urlHep: httpsUrl(raw.urlHep) } : {}),
  };
}

/**
 * Bảng trong bài: tối đa 5 cột, 20 hàng. Dòng đầu là tiêu đề cột. Không nhận HTML.
 * Thiếu tiêu đề cột hoặc không có hàng nào thì coi như không có bảng.
 */
function chuanHoaBang(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const headers = Array.isArray(raw.headers) ? raw.headers.map((x) => plain(x, 80)).slice(0, 5) : [];
  const cols = headers.length;
  if (cols < 2) return null;
  const rows = (Array.isArray(raw.rows) ? raw.rows : []).map((r) => {
    const cells = (Array.isArray(r) ? r : Array.isArray(r?.cells) ? r.cells : []).map((x) => plain(x, 600)).slice(0, cols);
    while (cells.length < cols) cells.push('');
    return { cells };
  }).filter((r) => r.cells.some(Boolean)).slice(0, 20);
  return rows.length ? { caption: plain(raw.caption, 200), headers, rows } : null;
}

/** Bảng ↔ chữ cho trang quản trị: mỗi dòng một hàng, các ô cách nhau bằng dấu |, dòng đầu là tiêu đề cột. */
export function bangSangChu(table) {
  if (!table) return '';
  return [table.headers, ...table.rows.map((r) => r.cells)].map((cells) => cells.join(' | ')).join('\n');
}
export function chuSangBang(text, caption = '') {
  const lines = String(text || '').split('\n').map((l) => l.trim()).filter(Boolean)
    .map((l) => l.replace(/^\||\|$/g, '').split('|').map((c) => c.trim()));
  if (lines.length < 2) return null;
  return chuanHoaBang({ caption, headers: lines[0], rows: lines.slice(1) });
}

function chuanHoaTrichDan(raw) {
  const text = plain(raw?.text, 400);
  return text ? { text, by: plain(raw.by, 120) } : null;
}

/** Đếm chữ trong toàn bài. Tiếng Việt tách theo tiếng nên đếm khoảng trắng là đủ. */
export function demChu(post) {
  const parts = [post.title, post.excerpt];
  for (const s of post.sections || []) {
    parts.push(s.heading, ...(s.paragraphs || []), ...(s.bullets || []), ...(s.notes || []), s.quote?.text, s.image?.caption);
    if (s.table) parts.push(...s.table.headers, ...s.table.rows.flatMap((r) => r.cells));
  }
  return parts.filter(Boolean).join(' ').replace(/\[\[(?:[^\]|]+\|)?([^\]]*)\]\]/g, '$1').split(/\s+/).filter(Boolean).length;
}

/**
 * So sánh bài đang có trên website với bản sắp lưu, liệt kê những thứ sẽ mất.
 *
 * Vì sao cần: trang quản trị lưu bằng setDoc — mảng sections bị thay hoàn toàn.
 * Nếu một tab quản trị mở từ phiên bản cũ (chưa biết bảng) lưu đè, toàn bộ bảng
 * trong bài biến mất mà không ai kịp nhận ra. Hàm này để chặn trước khi ghi.
 */
export function canhBaoMatNoiDung(cu, moi) {
  if (!cu) return [];
  const dem = (p, f) => (p?.sections || []).filter(f).length;
  const canh = [];
  const thieu = (ten, f) => { const d = dem(cu, f) - dem(moi, f); if (d > 0) canh.push(`${d} ${ten}`); };
  const dPhan = (cu.sections || []).length - (moi.sections || []).length;
  if (dPhan > 0) canh.push(`${dPhan} phần`);
  thieu('bảng', (s) => s.table);
  thieu('ảnh trong bài', (s) => s.image);
  const chuCu = demChu(cu);
  const chuMoi = demChu(moi);
  if (chuCu > 200 && chuMoi < chuCu * 0.7) canh.push(`khoảng ${Math.round((1 - chuMoi / chuCu) * 100)}% số chữ`);
  return canh;
}

/** Khoảng 230 tiếng/phút cho văn bản tiếng Việt có số liệu; mỗi ảnh cộng thêm ~10 giây. */
export function tinhThoiGianDoc(post) {
  const images = (post.sections || []).filter((s) => s.image).length + (post.coverImage ? 1 : 0);
  return Math.max(1, Math.round(demChu(post) / 230 + images / 6));
}

export function ngayVN(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

export const ORG_AUTHOR = 'Fundraising Vietnam';

export function chuanHoaBaiViet(raw = {}) {
  const sections = Array.isArray(raw.sections) ? raw.sections.slice(0, 20).map((section) => ({
    heading: plain(section?.heading, 160),
    paragraphs: Array.isArray(section?.paragraphs) ? section.paragraphs.map((x) => plain(x, 4000)).filter(Boolean).slice(0, 10) : [],
    bullets: Array.isArray(section?.bullets) ? section.bullets.map((x) => plain(x, 600)).filter(Boolean).slice(0, 20) : [],
    image: chuanHoaAnh(section?.image),
    quote: chuanHoaTrichDan(section?.quote),
    table: chuanHoaBang(section?.table),
    // Đoạn văn đứng ngay sau bảng (nhận xét, lưu ý).
    notes: Array.isArray(section?.notes) ? section.notes.map((x) => plain(x, 4000)).filter(Boolean).slice(0, 5) : [],
  })).filter((s) => s.heading || s.paragraphs.length || s.bullets.length || s.image || s.quote || s.table) : [];

  const post = {
    slug: chuanHoaSlug(raw.slug || raw.title),
    title: plain(raw.title, 180),
    excerpt: plain(raw.excerpt, 360),
    category: PLAYBOOK_CATEGORIES.includes(raw.category) ? raw.category : PLAYBOOK_CATEGORIES[0],
    stages: Array.isArray(raw.stages) ? raw.stages.map((x) => plain(x, 40)).filter(Boolean).slice(0, 5) : [],
    storyType: STORY_TYPES[raw.storyType] ? raw.storyType : 'huong-dan',
    templates: Array.isArray(raw.templates) ? raw.templates.map((x) => chuanHoaSlug(x)).filter(Boolean).slice(0, 4) : [],
    consentConfirmed: Boolean(raw.consentConfirmed),
    coverImage: httpsUrl(raw.coverImage),
    coverAlt: plain(raw.coverAlt, 200),
    coverCaption: plain(raw.coverCaption, 300),
    ogImage: httpsUrl(raw.ogImage),
    author: plain(raw.author || ORG_AUTHOR, 100),
    authorRole: plain(raw.authorRole, 140),
    status: raw.status === 'PUBLISHED' ? 'PUBLISHED' : 'DRAFT',
    featured: Boolean(raw.featured),
    sections,
    relatedTools: Array.isArray(raw.relatedTools) ? raw.relatedTools.map((x) => ({ slug: chuanHoaSlug(x?.slug), label: plain(x?.label, 100) })).filter((x) => x.slug).slice(0, 6) : [],
    sources: Array.isArray(raw.sources) ? raw.sources.map((x) => ({ label: plain(x?.label, 180), url: httpsUrl(x?.url) })).filter((x) => x.url).slice(0, 12) : [],
    seoTitle: plain(raw.seoTitle || raw.title, 180),
    seoDescription: plain(raw.seoDescription || raw.excerpt, 360),
    publishedAt: isoDate(raw.publishedAt) || new Date().toISOString().slice(0, 10),
    updatedAt: isoDate(raw.updatedAt),
  };
  // Loạt bài suy ra từ chủ đề (bài cũ lưu track 'cam-nang' cho mọi chủ đề cẩm nang
  // vẫn tự về đúng loạt mới). Câu chuyện founder và Spotlight luôn ở Tình huống thực tế.
  // Chủ đề lạ (không có trong danh sách) thì giữ track đã khai báo.
  const coChuDe = CATEGORIES.some((c) => c.name === post.category);
  post.track = CAN_DONG_Y.includes(post.storyType) ? 'cau-chuyen'
    : coChuDe ? trackOfCategory(post.category)
    : (TRACKS[raw.track] ? raw.track : 'cam-nang');
  // Thời gian đọc luôn tính từ nội dung thật, không nhập tay.
  post.readingMinutes = tinhThoiGianDoc(post);
  return post;
}

/** Kiểm tra trước khi lưu. Trả về danh sách lỗi (rỗng = hợp lệ). */
export function kiemTraBai(post, status) {
  const loi = [];
  if (!post.slug || !post.title || !post.excerpt || !post.sections.length) loi.push('Cần có tiêu đề, mô tả và ít nhất một phần nội dung.');
  if (RESERVED_SLUGS.includes(post.slug)) loi.push(`Đường dẫn "${post.slug}" được hệ thống dành riêng, hãy đổi tên khác.`);
  if (post.coverImage && !post.coverAlt) loi.push('Ảnh cover cần mô tả ảnh (alt).');
  post.sections.forEach((s, i) => { if (s.image && !s.image.alt) loi.push(`Ảnh ở phần ${i + 1} cần mô tả ảnh (alt).`); });
  if (status === 'PUBLISHED' && CAN_DONG_Y.includes(post.storyType) && !post.consentConfirmed) {
    loi.push(post.storyType === 'spotlight'
      ? 'Bài Spotlight cần xác nhận founder đã duyệt bản cuối trước khi xuất bản.'
      : 'Câu chuyện founder cần xác nhận nhân vật đã đồng ý cho đăng trước khi xuất bản.');
  }
  return loi;
}

/** Bài liên quan: ưu tiên cùng chủ đề, rồi cùng nhánh, rồi cùng giai đoạn. */
export function baiLienQuan(post, all, max = 3) {
  const score = (p) => (p.category === post.category ? 3 : 0) + (p.track === post.track ? 2 : 0)
    + ((p.stages || []).some((s) => post.stages?.includes(s)) ? 1 : 0);
  return all
    .filter((p) => p.slug !== post.slug && p.status !== 'DRAFT')
    .map((p) => ({ p, s: score(p) }))
    .sort((a, b) => b.s - a.s || String(b.p.publishedAt).localeCompare(String(a.p.publishedAt)))
    .slice(0, max)
    .map((x) => x.p);
}

/** Khung soạn sẵn cho bài câu chuyện — giữ mạch kể có cảm xúc và có bài học. */
export const KHUNG_CAU_CHUYEN = [
  { heading: 'Bối cảnh', hint: 'Ngành, vòng vốn, số tiền mặt, số người, thời điểm. Càng cụ thể càng đáng tin.' },
  { heading: 'Quyết định khó', hint: 'Founder đứng trước lựa chọn gì? Vì sao cả hai hướng đều có giá?' },
  { heading: 'Điều không như dự tính', hint: 'Sai ở đâu, bị từ chối thế nào, cảm giác lúc đó ra sao.' },
  { heading: 'Bước ngoặt', hint: 'Điều gì thay đổi: một cuộc gặp, một con số, một cách trình bày khác.' },
  { heading: 'Kết quả', hint: 'Kết quả thật, kể cả khi vòng gọi vốn không thành.' },
  { heading: 'Nếu làm lại', hint: '3–5 bài học founder khác áp dụng được ngay.' },
];

/**
 * Khung soạn sẵn cho bài Startup Spotlight. Lớp công khai: kể chuyện sản phẩm
 * và founder. KHÔNG đưa điều khoản gọi vốn (quy mô vòng, định giá, séc) vào
 * bài; phần đó chỉ gửi riêng cho nhà đầu tư. Xem tài liệu "Startup Spotlight:
 * mẫu thẻ và tiêu chí pilot".
 */
export const KHUNG_SPOTLIGHT = [
  { heading: 'Startup làm gì', hint: 'Một câu định vị: làm gì, cho ai (tối đa 20 chữ). Thêm ảnh sản phẩm thật ở phần này, kiểu "cắt 16:9". Mô tả ngắn của bài nên dùng lại đúng câu này.' },
  { heading: 'Vấn đề', hint: '2 câu, nêu khách hàng cụ thể và nỗi đau đo được (mất bao nhiêu giờ, bao nhiêu tiền).' },
  { heading: 'Sản phẩm giải quyết thế nào', hint: '2 đến 3 câu, hoặc trước và sau. Có thể thêm ảnh chụp màn hình kiểu "giữ nguyên tỷ lệ", hoặc link video demo 60 đến 90 giây ở đoạn cuối.' },
  { heading: 'Bằng chứng tiến triển', hint: 'Dùng bảng: Chỉ số | Giá trị | Thời điểm | Nguồn. Nguồn ghi "founder tự khai" hoặc "đã đối chiếu". Chưa có doanh thu thì dùng khách thử nghiệm, kết quả pilot, thư cam kết.' },
  { heading: 'Đội ngũ sáng lập', hint: 'Mỗi người một dòng ở ô Danh sách: tên, vai trò, vì sao hiểu bài toán này.' },
  { heading: 'Đang tìm kiếm', hint: 'Ngoài vốn: khách hàng thử nghiệm, đối tác, nhân sự. Chỉ ghi "đang mở kết nối nhà đầu tư", không ghi số vốn hay định giá.' },
];

export const BANG_TIEN_TRIEN_MAU = 'Chỉ số | Giá trị | Thời điểm | Nguồn\n | | | Founder tự khai';
